import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CaretLeft, CaretRight, CalendarBlank, Fire, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { startOfWeek, addDays, todayLocalDate, weekdayLabel, formatShortDate } from "@/lib/time";
import { meta, MANUAL_STATUSES, STATUS_META } from "@/lib/attendance";
import { AttendanceHistoryDialog } from "@/components/AttendanceHistoryDialog";

const SUMMARY = [
    ["worked", "Worked"],
    ["no_schedule", "No Schedule"],
    ["time_off", "Time Off"],
    ["absent", "Absent"],
    ["unclassified", "Unclassified"],
];

export default function AdminAttendance() {
    const [weekStart, setWeekStart] = useState(() => startOfWeek(todayLocalDate()));
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [search, setSearch] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedDates, setSelectedDates] = useState([]);
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [detailId, setDetailId] = useState(null);
    const [reloadTick, setReloadTick] = useState(0);

    const range = useMemo(() => {
        if (customFrom && customTo) return { from: customFrom, to: customTo };
        return { from: weekStart, to: addDays(weekStart, 6) };
    }, [weekStart, customFrom, customTo]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`/attendance/grid?date_from=${range.from}&date_to=${range.to}`)
            .then(({ data: d }) => { if (!cancelled) setData(d); })
            .catch((e) => {
                if (cancelled) return;
                setData(null);  // never leave stale rows under a mismatching label
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [range.from, range.to, reloadTick]);

    const employees = useMemo(() => {
        const list = Array.isArray(data?.employees) ? data.employees : [];
        const q = search.trim().toLowerCase();
        return q ? list.filter((e) => e.full_name.toLowerCase().includes(q)) : list;
    }, [data, search]);
    const dates = useMemo(() => data?.dates || [], [data]);

    // Totals always describe what is on screen, so they follow the search filter.
    const totals = useMemo(() => {
        const t = { worked: 0, no_schedule: 0, time_off: 0, absent: 0, unclassified: 0 };
        employees.forEach((emp) => dates.forEach((d) => {
            const status = emp.days?.[d]?.status || "unclassified";
            t[status] = (t[status] || 0) + 1;
        }));
        return t;
    }, [employees, dates]);

    const toggle = (setter) => (value) =>
        setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

    const allEmployeesSelected = employees.length > 0 && employees.every((e) => selectedEmployees.includes(e.technician_id));

    const apply = useCallback(async (status, employeeIds, dateList) => {
        if (!employeeIds.length || !dateList.length) return;
        setSaving(true);
        try {
            const { data: res } = await api.post("/attendance/bulk", {
                technician_ids: employeeIds,
                dates: dateList,
                status,
                note: note.trim(),
            });
            const parts = [`${res.updated} day${res.updated === 1 ? "" : "s"} marked ${res.label}`];
            if (res.skipped_worked) parts.push(`${res.skipped_worked} skipped (already Worked)`);
            if (res.unchanged) parts.push(`${res.unchanged} unchanged`);
            toast.success(parts.join(" · "));
            setSelectedEmployees([]);
            setSelectedDates([]);
            setNote("");
            setReloadTick((t) => t + 1);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    }, [note]);

    const shiftWeek = (delta) => {
        setCustomFrom(""); setCustomTo("");
        setWeekStart((w) => addDays(w, delta * 7));
    };

    return (
        <div className="space-y-6">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">Time Tracking</div>
                <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                    Attendance Status
                </h1>
                <p className="text-slate-600 mt-2 text-sm">
                    Worked days come straight from Clock In records and can't be edited here. Classify the days
                    without a Clock In — attendance never touches payroll or Time Records.
                </p>
            </div>

            {/* Range controls */}
            <Card className="border-[#E2E8F0]">
                <CardContent className="p-4 sm:p-5 flex flex-col lg:flex-row lg:items-end gap-4">
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="icon" onClick={() => shiftWeek(-1)}
                            data-testid="attendance-prev-week"><CaretLeft size={16} /></Button>
                        <div className="px-3 py-2 rounded-md bg-slate-50 border border-[#E2E8F0] text-sm font-medium text-[#003366] whitespace-nowrap flex items-center gap-2">
                            <CalendarBlank size={15} className="text-[#0066D9]" />
                            {formatShortDate(range.from)} → {formatShortDate(range.to)}
                        </div>
                        <Button variant="outline" size="icon" onClick={() => shiftWeek(1)}
                            data-testid="attendance-next-week"><CaretRight size={16} /></Button>
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="space-y-1.5">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">From</Label>
                            <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                                className="h-10 w-40 border-[#E2E8F0]" data-testid="attendance-from-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">To</Label>
                            <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                                className="h-10 w-40 border-[#E2E8F0]" data-testid="attendance-to-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Employee</Label>
                            <Input placeholder="Search name…" value={search} onChange={(e) => setSearch(e.target.value)}
                                className="h-10 w-48 border-[#E2E8F0]" data-testid="attendance-search-input" />
                        </div>
                        {(customFrom || customTo || search) && (
                            <Button variant="ghost" className="h-10 text-slate-500"
                                onClick={() => { setCustomFrom(""); setCustomTo(""); setSearch(""); }}
                                data-testid="attendance-clear-filters">Clear</Button>
                        )}
                    </div>
                    <div className="lg:ml-auto flex flex-wrap gap-2">
                        {SUMMARY.map(([key, label]) => (
                            <Badge key={key} className={`${meta(key).badge} border text-[11px]`}
                                data-testid={`attendance-total-${key}`}>
                                {label}: {totals[key] ?? 0}
                            </Badge>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Bulk action bar */}
            {(selectedEmployees.length > 0 || selectedDates.length > 0) && (
                <Card className="border-[#0066D9]/40 bg-blue-50/50" data-testid="attendance-bulk-bar">
                    <CardContent className="p-4 flex flex-col lg:flex-row lg:items-center gap-3">
                        <div className="text-sm text-[#003366] font-medium whitespace-nowrap">
                            {selectedEmployees.length} employee{selectedEmployees.length === 1 ? "" : "s"} ·{" "}
                            {selectedDates.length} date{selectedDates.length === 1 ? "" : "s"} selected
                        </div>
                        <Input placeholder="Optional note (saved in the audit trail)" value={note}
                            onChange={(e) => setNote(e.target.value)}
                            className="h-10 border-[#E2E8F0] bg-white lg:max-w-sm" data-testid="attendance-note-input" />
                        <div className="flex flex-wrap gap-2 lg:ml-auto">
                            {MANUAL_STATUSES.map((s) => (
                                <Button key={s} size="sm" variant="outline" disabled={saving || !selectedEmployees.length || !selectedDates.length}
                                    className={`${meta(s).badge} border hover:opacity-80`}
                                    onClick={() => apply(s, selectedEmployees, selectedDates)}
                                    data-testid={`attendance-bulk-${s}`}>
                                    Mark {meta(s).short}
                                </Button>
                            ))}
                            <Button size="sm" variant="ghost" disabled={saving}
                                onClick={() => apply("unclassified", selectedEmployees, selectedDates)}
                                data-testid="attendance-bulk-unclassified">Clear</Button>
                            <Button size="sm" variant="ghost" className="text-slate-500"
                                onClick={() => { setSelectedEmployees([]); setSelectedDates([]); }}
                                data-testid="attendance-bulk-cancel">Deselect</Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Grid */}
            <Card className="border-[#E2E8F0]">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-6"><Skeleton className="h-72 rounded-md" /></div>
                    ) : employees.length === 0 ? (
                        <div className="p-10 text-center text-sm text-slate-500" data-testid="attendance-grid-empty">
                            {data ? "No employees match this filter." : "No attendance data for this range. Pick a shorter range (max 92 days) and try again."}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="attendance-grid">
                                <thead>
                                    <tr className="border-b border-[#E2E8F0] bg-slate-50">
                                        <th className="p-3 text-left w-8">
                                            <Checkbox checked={allEmployeesSelected}
                                                onCheckedChange={(v) => setSelectedEmployees(v ? employees.map((e) => e.technician_id) : [])}
                                                data-testid="attendance-select-all-employees" />
                                        </th>
                                        <th className="p-3 text-left text-xs uppercase tracking-wider text-slate-500 min-w-[180px]">
                                            Employee
                                        </th>
                                        {dates.map((d) => (
                                            <th key={d} className="p-2 text-center min-w-[92px]">
                                                <label className="flex flex-col items-center gap-1 cursor-pointer">
                                                    <span className="text-[10px] uppercase tracking-wider text-slate-500">
                                                        {weekdayLabel(d)}
                                                    </span>
                                                    <span className="text-xs font-semibold text-[#003366]">{formatShortDate(d)}</span>
                                                    <Checkbox checked={selectedDates.includes(d)}
                                                        onCheckedChange={() => toggle(setSelectedDates)(d)}
                                                        data-testid={`attendance-select-date-${d}`} />
                                                </label>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {employees.map((emp) => (
                                        <tr key={emp.technician_id} className="border-b border-slate-100 hover:bg-slate-50/60"
                                            data-testid={`attendance-row-${emp.technician_id}`}>
                                            <td className="p-3">
                                                <Checkbox checked={selectedEmployees.includes(emp.technician_id)}
                                                    onCheckedChange={() => toggle(setSelectedEmployees)(emp.technician_id)}
                                                    data-testid={`attendance-select-employee-${emp.technician_id}`} />
                                            </td>
                                            <td className="p-3">
                                                <button className="text-left" onClick={() => setDetailId(emp.technician_id)}
                                                    data-testid={`attendance-employee-link-${emp.technician_id}`}>
                                                    <div className="font-medium text-[#1F2A44] hover:text-[#0066D9]">{emp.full_name}</div>
                                                    <div className="text-[11px] text-slate-500 flex items-center gap-1">
                                                        <Fire size={11} weight="fill" className="text-[#E6002C]" />
                                                        {emp.current_streak} day streak
                                                        {emp.in_recognition === false && (
                                                            <span className="ml-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold"
                                                                title="Excluded from attendance rankings and recognition"
                                                                data-testid={`attendance-not-ranked-${emp.technician_id}`}>
                                                                Not ranked
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>
                                            </td>
                                            {dates.map((d) => {
                                                const cell = emp.days?.[d] || { status: "unclassified" };
                                                const m = meta(cell.status);
                                                const isWorked = cell.status === "worked";
                                                return (
                                                    <td key={d} className="p-1.5 text-center">
                                                        {isWorked ? (
                                                            <div className={`${m.cell} border rounded-md py-1.5 text-[11px] font-medium`}
                                                                title={cell.shifts ? `${cell.shifts} shift(s)` : ""}
                                                                data-testid={`attendance-cell-${emp.technician_id}-${d}`}>
                                                                {m.short}
                                                            </div>
                                                        ) : (
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <button className={`${m.cell} border rounded-md py-1.5 w-full text-[11px] font-medium hover:opacity-80`}
                                                                        title={cell.note || ""}
                                                                        data-testid={`attendance-cell-${emp.technician_id}-${d}`}>
                                                                        {m.short}
                                                                    </button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="center">
                                                                    {MANUAL_STATUSES.map((s) => (
                                                                        <DropdownMenuItem key={s} onClick={() => apply(s, [emp.technician_id], [d])}
                                                                            data-testid={`attendance-set-${s}-${emp.technician_id}-${d}`}>
                                                                            <span className={`h-2 w-2 rounded-full mr-2 ${STATUS_META[s].dot}`} />
                                                                            Mark {STATUS_META[s].short}
                                                                        </DropdownMenuItem>
                                                                    ))}
                                                                    <DropdownMenuItem onClick={() => apply("unclassified", [emp.technician_id], [d])}
                                                                        data-testid={`attendance-set-unclassified-${emp.technician_id}-${d}`}>
                                                                        <span className="h-2 w-2 rounded-full mr-2 bg-amber-400" />
                                                                        Back to Unclassified
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <p className="text-xs text-slate-400 flex items-center gap-1.5">
                <ClipboardText size={14} />
                Every classification is stored with the previous status, the admin who changed it and a timestamp.
                Days with a Clock In are always shown as Worked and are skipped by bulk actions.
            </p>

            <AttendanceHistoryDialog
                technicianId={detailId}
                dateFrom={range.from}
                dateTo={range.to}
                open={Boolean(detailId)}
                onOpenChange={(o) => { if (!o) setDetailId(null); }}
            />
        </div>
    );
}
